import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, styled } from 'styled-components';
import * as Ariakit from '@ariakit/react';

import { theme } from '../../../styles';
import { CheckboxIcon, ChevronIconOld, MagnifyingGlassIcon, MenuCloseIcon, StarIcon } from '../../../assets/icons';
import { Animate } from '../../Animate';
import { Text } from '../../Text';
import { Button } from '../../Button/Button';
import { Container } from '../../Layout';
import { ErrorMessage } from '../ErrorMessage';
import { defaultItemsLimit, OptionType, SelectMultiProps, SelectProps, SelectSingleProps } from './types';

export const StyledSelectPopover = styled(Ariakit.SelectPopover)`
  z-index: ${theme.zIndex.general};
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
`;

export const SelectListStyle = css`
  background-color: ${theme.colors.white};
  max-height: 20rem;
  overflow: auto;
  overscroll-behavior: contain;
  &:focus-visible {
    outline: ${theme.colors.foraBlue} solid 2px;
    border-radius: 0;
    outline-offset: 0;
  }
`;
const StyledSelectList = styled(Ariakit.SelectList)`
  ${SelectListStyle}
`;

export const SearchResultsFooter = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  ${theme.typography.textStandard}
  padding: 0.875rem 1.25rem 0.875rem 1.25rem;
  background-color: ${theme.colors.black05};
  box-sizing: border-box;
  width: 100%;
  height: 42px;
`;

export const FavoriteButton = styled(Button)`
  position: absolute;
  right: 10px;
  top: 0;
  height: 100%;
`;
const SearchIconWrapper = styled.div`
  position: absolute;
  right: 18px;
  top: 0;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledSearchInput = styled(Ariakit.Combobox)`
  ${theme.typography.textStandard}
  padding: 0.875rem 1.25rem 0.875rem 1.25rem;
  background-color: ${theme.colors.black05};
  box-sizing: border-box;
  width: 100%;
  height: 50px;
  border: none;
  border-bottom: 1px solid ${theme.colors.black20};
  &:focus {
    outline: none;
    border-bottom: ${theme.colors.foraBlue} solid 2px;
  }
`;

export const StyledSelectHeader = styled(Ariakit.Select)<{ disabled?: boolean; error?: string }>`
  ${theme.typography.textStandard}
  display: block;
  position: relative;
  width: 100%;
  height: 50px;
  padding: 0 2.5rem 0 1.25rem;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.black60};
  border-radius: 2px;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus-visible {
    outline: ${theme.colors.foraBlue} solid 2px;
    border-radius: 2px;
    outline-offset: -1px;
  }

  ${({ error }) =>
    error &&
    css`
      background-color: ${theme.colors.babyPink};
      border: 2px solid ${theme.colors.burgundy};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: transparent;
      border: 1px solid ${theme.colors.black30};
      cursor: default;
    `}

  @media screen and (max-width: ${theme.breakpoint.lg - 1}px) {
    height: 60px;
  }
`;

export const SelectItemStyle = css`
  position: relative;
  ${theme.typography.textStandard}
  padding: 10px 20px;
  display: flex;
  align-content: center;
  border: none;
  text-align: left;
  outline: none;
  appearance: none;
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.black20};
  }

  &:hover,
  &[data-active-item],
  &:focus {
    background-color: ${theme.colors.blueHover};
  }
`;

const StyledSelectItem = styled(Ariakit.SelectItem)`
  ${SelectItemStyle}
  padding: 14px 20px;
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    min-height: 60px;
  }
`;

export const ChevronIconWrapper = styled.div`
  position: absolute;
  right: 1.375rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
`;

const SelectWrapper = ({
  testId,
  options,
  hasError,
  errorMessage,
  errorVariant,
  placeholder,
  disabled,
  search,
  searchPlaceholder = 'Sök...',
  showFavorites,
  showIcon,
  searchResetText = 'Rensa sökning',
  multiple,
  itemsLimit = defaultItemsLimit,
  onToggleFavorite,
  selectStore,
}: SelectProps & {
  selectStore: Ariakit.SelectStore<string | string[]>;
  multiple?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const showSearch = search || options.length > itemsLimit;

  const open = Ariakit.useStoreState(selectStore, 'open');
  const value = Ariakit.useStoreState(selectStore, 'value');

  const resetSearch = () => {
    if (searchRef?.current) {
      searchRef?.current.focus();
    }
    setSearchValue('');
  };

  useEffect(() => {
    // Create a timer each time searchValue (input) changes
    const debounceTimer = setTimeout(() => {
      if (searchValue.length >= 2 || searchValue === '') setDebouncedSearchValue(searchValue);
    }, 300);
    // Clear timeount when the hook "dies", i.e. when searchValue changes,
    // the old timer is cleared before the new version of the hook is run and a new timer is created
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchValue]);

  const matches = useMemo(() => {
    if (debouncedSearchValue) {
      const searchLower = debouncedSearchValue.toLowerCase();

      const filteredOptions = options.filter(e => {
        const nameMatch = e.name.toLowerCase().includes(searchLower);
        if (nameMatch) return true;
        const descriptionMatch = e.description?.toLowerCase().includes(searchLower);
        return descriptionMatch;
      });

      if (showFavorites) {
        return filteredOptions.sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1));
      }
      return filteredOptions;
    }

    if (showFavorites) {
      // Avoid sorting on the original array, since it would mutate a prop. Practically, it would permanently change the original sorting of the options.
      return [...options].sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1));
    }
    return options;

    // Open as a dependency to sort favorite at open/close
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, debouncedSearchValue, open, showFavorites]);

  if (showSearch) {
    return (
      <Ariakit.ComboboxProvider resetValueOnHide value={searchValue} setValue={setSearchValue}>
        <Ariakit.SelectProvider store={selectStore}>
          <SelectHeader
            options={matches}
            placeholder={placeholder}
            resetSearch={resetSearch}
            open={open}
            value={value}
            hasError={hasError}
            disabled={disabled}
            testId={testId}
          />
          <StyledSelectPopover sameWidth flip={false} fixed={true}>
            {!!showSearch && (
              <SelectSearchInput
                searchPlaceholder={searchPlaceholder}
                searchValue={searchValue}
                resetSearch={resetSearch}
                searchRef={searchRef}
              />
            )}
            <SelectList
              value={value}
              multiple={multiple}
              onToggleFavorite={onToggleFavorite}
              showFavorites={showFavorites}
              optionList={matches}
              search={showSearch}
              showIcon={showIcon}
              itemsLimit={itemsLimit}
            />
            {(options.length > itemsLimit || debouncedSearchValue) && (
              <ResultFooter
                options={options}
                matches={matches}
                searchResetText={debouncedSearchValue ? searchResetText : ''}
                resetSearch={resetSearch}
                itemsLimit={itemsLimit}
              />
            )}
          </StyledSelectPopover>
          {hasError && !disabled && (
            <ErrorMessage testId={`${testId}-error`} variant={errorVariant}>
              {errorMessage}
            </ErrorMessage>
          )}
        </Ariakit.SelectProvider>
      </Ariakit.ComboboxProvider>
    );
  } else {
    return (
      <Ariakit.SelectProvider store={selectStore}>
        <SelectHeader
          options={options}
          placeholder={placeholder}
          resetSearch={resetSearch}
          open={open}
          value={value}
          hasError={hasError}
          disabled={disabled}
          testId={testId}
        />
        <StyledSelectPopover sameWidth flip={false} fixed={true}>
          <SelectList
            value={value}
            multiple={multiple}
            onToggleFavorite={onToggleFavorite}
            showFavorites={showFavorites}
            optionList={matches}
            search={showSearch}
            showIcon={showIcon}
            itemsLimit={itemsLimit}
          />
          {options.length > itemsLimit && (
            <ResultFooter options={options} matches={matches} searchResetText="" itemsLimit={itemsLimit} />
          )}
        </StyledSelectPopover>
        {hasError && !disabled && (
          <ErrorMessage testId={`${testId}-error`} variant={errorVariant}>
            {errorMessage}
          </ErrorMessage>
        )}
      </Ariakit.SelectProvider>
    );
  }
};

const SelectList = ({
  optionList,
  itemsLimit = defaultItemsLimit,
  ...rest
}: Pick<SelectProps, 'onToggleFavorite' | 'showFavorites' | 'showIcon' | 'search' | 'itemsLimit'> & {
  optionList: OptionType[];
  value?: string[] | string;
  multiple?: boolean;
}) => {
  const listIndicesToRender = useMemo(() => {
    return Array.from({ length: Math.min(itemsLimit, optionList.length) });
  }, [itemsLimit, optionList.length]);

  return (
    <StyledSelectList>
      {listIndicesToRender.map((_, index) => (
        <SelectItem optionList={optionList} item={optionList[index]} {...rest} key={index} />
      ))}
    </StyledSelectList>
  );
};

const SelectItem = ({
  multiple,
  showIcon,
  showFavorites,
  search,
  value,
  item,
  onToggleFavorite,
}: Pick<SelectProps, 'onToggleFavorite' | 'showFavorites' | 'showIcon' | 'search' | 'itemsLimit'> & {
  optionList: OptionType[];
  value?: string[] | string;
  multiple?: boolean;
  item: OptionType;
}) => {
  const onClickFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onToggleFavorite?.(item);
  };

  const getItem = useCallback(() => ({ ...item }), [item]);
  return (
    <StyledSelectItem
      role="option"
      id={`option-${item.id}`}
      data-testid={`test-option-${item.id}`}
      value={item.id}
      key={item.id}
      disabled={item.isDisabled}
      getItem={getItem}
      render={search ? <Ariakit.ComboboxItem /> : <Ariakit.SelectItem />}
    >
      {multiple && !item.noMultiSelect && (
        <CheckboxIcon
          variant={value?.includes(item.id) ? 'checked' : ''}
          color={item.isDisabled ? 'black60' : 'black'}
          width={22}
          marginRight={10}
        />
      )}
      {showIcon && (
        <Container height={22} marginRight={10}>
          {item.icon}
        </Container>
      )}
      <Container>
        <Text color={item.isDisabled ? 'black60' : 'black'} fontWeight={item.bold ? 'semibold' : 'normal'}>
          {item.name}
        </Text>
        {item.description && <Text color="black70">{item.description}</Text>}
      </Container>
      {showFavorites && (
        <FavoriteButton styleAs="link" onClick={onClickFavorite}>
          <StarIcon variant={item.favorite ? 'fill' : ''} color={'black60'} width={22} />
        </FavoriteButton>
      )}
    </StyledSelectItem>
  );
};

const SelectSearchInput = ({
  searchPlaceholder,
  searchValue,
  resetSearch,
  searchRef,
}: Pick<SelectProps, 'searchPlaceholder'> & {
  searchValue: string;
  searchRef: React.MutableRefObject<HTMLInputElement | null>;
  resetSearch: () => void;
}) => {
  return (
    <>
      <SearchIconWrapper>
        {searchValue ? (
          <Button styleAs="link" onClick={resetSearch}>
            <MenuCloseIcon color={'black'} width={14} />
          </Button>
        ) : (
          <MagnifyingGlassIcon color={'black'} width={14} />
        )}
      </SearchIconWrapper>
      <StyledSearchInput ref={searchRef} autoSelect placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
    </>
  );
};

const SelectHeader = ({
  open,
  placeholder,
  disabled,
  value,
  hasError,
  options,
  testId,
}: Pick<SelectProps, 'hasError' | 'disabled' | 'placeholder' | 'testId'> & {
  resetSearch: () => void;
  open: boolean;
  value?: string[] | string;
  options: OptionType[];
}) => {
  const renderValue = (value: string | string[]) => {
    if (Array.isArray(value)) {
      const names = options.filter(({ id }) => value.includes(id)).map(e => e.name);
      return names.join(', ');
    }
    // TODO: use item.get() here before using find
    return options.find(o => o.id === value)?.name;
  };
  return (
    <StyledSelectHeader
      role="listbox"
      data-testid={testId}
      type="button"
      disabled={disabled}
      error={hasError ? 'error' : ''}
    >
      {value?.length ? <Ariakit.SelectValue>{renderValue}</Ariakit.SelectValue> : placeholder}
      <ChevronIconWrapper>
        <Animate.Rotate isTriggered={open} degrees={180}>
          <ChevronIconOld
            variant="thin"
            direction="downward"
            width={16}
            containerSize={20}
            color={disabled ? 'black30' : 'black'}
          />
        </Animate.Rotate>
      </ChevronIconWrapper>
    </StyledSelectHeader>
  );
};

const ResultFooter = ({
  searchResetText,
  resetSearch,
  matches,
  options,
  itemsLimit = defaultItemsLimit,
}: Pick<SelectProps, 'searchResetText' | 'options' | 'itemsLimit'> & {
  matches: OptionType[];
  resetSearch?: () => void;
}) => {
  return (
    <SearchResultsFooter>
      <Text fontWeight="semibold">{`${itemsLimit < matches.length ? itemsLimit : matches.length} av ${options.length}`}</Text>
      {searchResetText && (
        <Container>
          <Button fullWidth={false} styleAs="link" onClick={resetSearch}>
            {searchResetText}
          </Button>
        </Container>
      )}
    </SearchResultsFooter>
  );
};

export const SelectMulti = ({
  selectedIds,
  onSelect,
  testId,
  options,
  itemsLimit = defaultItemsLimit,
  ...props
}: SelectProps & SelectMultiProps) => {
  const [open, setOpen] = useState(false);

  const onChange = (value?: string | string[]) => {
    if (Array.isArray(value)) {
      let selectedOptions = [] as OptionType[];

      value.forEach((id, i) => {
        const option = options.find(e => e.id === id);
        if (option) {
          selectedOptions.push(option);
        }
      });

      const noMultiSelectOption = selectedOptions.at(-1)?.noMultiSelect ? selectedOptions.at(-1) : null;
      // If "noMultiSelect" set only this option.
      if (noMultiSelectOption) {
        setOpen(false);
        onSelect([noMultiSelectOption]); // ??
        return;
      }
      // If same noMultiSelectOption don't change state.
      if (!value.length && options.find(e => e.id === selectedIds.at(-1))?.noMultiSelect) {
        setOpen(false);
        return;
      }
      // Remove noMultiSelect options.
      selectedOptions = selectedOptions.filter(e => !e.noMultiSelect);

      onSelect(selectedOptions);
      return;
    }
  };

  const selectStore = Ariakit.useSelectStore({ open, setOpen, value: selectedIds, setValue: onChange });

  return (
    <SelectWrapper
      {...props}
      itemsLimit={itemsLimit}
      testId={testId}
      options={options}
      multiple={true}
      selectStore={selectStore}
    />
  );
};

// 171932 remove
/** @deprecated use NewSelect */
export const Select = ({ options, ...props }: SelectProps & SelectSingleProps) => (
  <SelectWrapper options={options} {...props} />
);
